/**
 * Utils de imagen: normalizacion de la FORMA en que una foto entra o sale de la app
 * (URI nativo del FS, dataURL, blob URL, URL http).
 */

/** ¿Es un URI del FS nativo (Capacitor iOS/Android)? Son los que NO se pueden pasar a un <img> ni a loadFromUrl. */
export const isNativeUri = v =>
  typeof v === 'string' && (v.startsWith('file://') || v.startsWith('content://'))

/**
 * Normaliza el valor que emite PhotoEditor.save() a una forma renderizable en <img>/q-img
 * y aceptada por el backend (dataURL o URL http). En movil el editor emite file://|content://,
 * que el WebView no puede pintar: se lee el binario del FS y se entrega como dataURL.
 *
 * @param {*} value  payload del evento save (dataURL | file://|content:// | http(s) | blob: | null)
 * @param {object} deps
 * @param {Function} deps.readNativeImage  (uri) => Promise<string|null> — lee el FS a dataURL.
 *                                         Se invoca SOLO en la rama nativa (import perezoso).
 * @returns {Promise<*>} dataURL para URIs nativos (null si el binario no se pudo leer);
 *                       cualquier otra forma se devuelve intacta.
 */
export async function resolveSavedImage (value, { readNativeImage }) {
  if (isNativeUri(value)) {
    const base64 = await readNativeImage(value)
    return (typeof base64 === 'string' && base64.startsWith('data:image')) ? base64 : null
  }
  return value
}

/**
 * Cadena de filtros que el backend antepone a la ruta del objeto en S3 al derivar una variante
 * Thumbor, por ejemplo:
 *   https://cdn/filters:format(jpeg)/filters:quality(70)/fit-in/700x700/2026/06/foo.jpeg
 * Quitarla deja la ruta del JPEG ORIGINAL de camara:
 *   https://cdn/2026/06/foo.jpeg
 */
const THUMBOR_VARIANT = /\/filters:format\(jpeg\)\/filters:quality\(\d+\)\/fit-in\/[^/]+\//

/**
 * Deriva la URL del ORIGINAL a partir de una URL de variante Thumbor.
 *
 * El original pesa varios MB (es la foto tal cual salio de la camara) y la variante pesa decenas
 * de KB, asi que esta funcion SOLO debe alimentar superficies que necesitan resolucion completa:
 * el editor de fotos, el visor a pantalla completa y la descarga manual. Para pintar una miniatura
 * se usa la URL Thumbor tal cual llega (ver TKT-2240: pintar miniaturas con el original disparaba
 * megabytes de datos moviles por cada item-foto renderizado).
 *
 * Es no-op cuando la URL no es una variante Thumbor (blob:, data:, file://, ruta ya original) o
 * cuando el CDN de miniaturas no esta configurado en el entorno.
 *
 * @param {*} url  valor crudo de la respuesta/addon (URL Thumbor, blob:, data:, file://, null…)
 * @returns {*} la URL del original si habia filtros que quitar; el valor intacto en cualquier otro caso.
 */
export function toOriginalImageUrl (url) {
  if (typeof url !== 'string' || !url.includes('filters:quality')) return url
  if (import.meta.env.CLOUDFRONT_THUMBNAIL_URL === undefined) return url
  return url.replace(THUMBOR_VARIANT, '/')
}
